import type { Contact, Passenger } from '../../api/types/common';
import type { CorporateEmployee } from '../../api/types/corporateEmployee';
import type { CurrentAccount } from '../../api/types/reports';
import type { BillingInformation } from '../../api/types/billing';
import { submitBilling } from '../../api/endpoints/billing';
import { redirectToBankIfNeeded } from '../../components/payment/paymentRedirect';
import { cartIsPaid, type PersistedBookingFlowConfig } from '../../booking-flow/resume';
import { busPersistConfig } from './resume';
import {
  allocateBus,
  finalizeBusShopping,
  getBusSchema,
  makeBusPayment,
  makeBusPreBooking,
  searchBusTrips,
  updateBusPassengers,
} from '../../api/endpoints/bus';
import type {
  BusLocationOption,
  BusSchemaResponse,
  BusSearchResponse,
  BusSeat,
  BusShoppingCart,
  BusVehicle,
  PaymentForm,
  PaymentResponse,
  PaymentType,
} from '../../api/types/bus';
import { busVehicleJourneyDate, busVehicleJourneyNo } from './tripFields';

/**
 * Bus flow (migration guide Bölüm 6.1): Search → GetBusSchema (seat selection) →
 * Allocate → UpdatePassengers → MakePreBooking → FinalizeShopping → MakePayment →
 * Document. Each step's `run` uses the accumulated state from prior steps and returns
 * the patch that feeds the next one (shoppingCartId flows from Allocate onward).
 *
 * Arama formu uçuş aramasındaki gibi tek yön / gidiş-dönüş (`BusSearchFormValues.tripType`)
 * seçtiriyor ve `retDate`'i yalnız gidiş-dönüşte `Bus/Search`'e gönderiyor. `Bus/Search`
 * yanıtı yönleri ayırmıyor (`vehicles` düz dizi) + bu ortamda envanter yok → dönüş
 * seferinin ayrı seçimi / iki yönlü `allocatedDirections` HENÜZ YOK; akış tek sefer
 * seçip devam ediyor. Gerçek gidiş-dönüş rezervasyonu envanterli bir hesap gerektirir.
 */
export type BusTripType = 'OneWay' | 'RoundTrip';

export interface BusSearchFormValues {
  origin?: BusLocationOption;
  destination?: BusLocationOption;
  /** Uçuş aramasındaki gibi tek yön / gidiş-dönüş seçimi — `retDate` yalnız
   *  `'RoundTrip'` iken doldurulur ve `Bus/Search`'e gönderilir. */
  tripType?: BusTripType;
  /** ISO yyyy-MM-dd */
  depDate: string;
  retDate?: string;
  passengerCount: number;
  employee?: CorporateEmployee;
}

export interface BusFlowState {
  searchForm: BusSearchFormValues;
  searchResult?: BusSearchResponse;
  selectedTrip?: BusVehicle;
  seatSchema?: BusSchemaResponse;
  selectedSeats: BusSeat[];
  shoppingCartId?: string;
  shoppingCart?: BusShoppingCart;
  passengers: Passenger[];
  contact?: Contact;
  /** Cari hesap (`Report/FindCurrentAccount`) — seçilirse yolcu adımından sonra
   *  `Billing/Submit` kurumsal fatura ile çağrılır ve ödeme adımında "Cari ödeme" açılır. */
  selectedCurrentAccount?: CurrentAccount;
  /** Yolcu formundaki fatura kartında derlenir, `Bus/UpdatePassengers` başarılı olduktan
   *  hemen sonra `Billing/Submit`'e gönderilir (uçuş akışıyla aynı sıra). */
  billingInformation?: BillingInformation;
  paymentForm?: PaymentForm;
  /** 'CurrentPayment' seçiliyse `paymentForm` gerekmez — hesap Billing/Submit ile bağlandı. */
  paymentType?: PaymentType;
  paymentAmount?: number;
  payment?: PaymentResponse;
  /** Kurumsal bütçe/politika dışı otobüs bileti yolcu adımı "Onaya Gönder" ile tamamlandı.
   *  `Bus/UpdatePassengers` kurumsal + `PolicySendApproval` sepette `PendingBus` kaydı
   *  oluşturur (2026-09-09 backend eklendi) ve `ShoppingCart.ApprovalStatus = Pending` yapar;
   *  rezervasyon yöneticinin `PendingBus/GetPendingBuses` listesine düşer. `BusFlowPage` bunu
   *  görünce bilgi verip rezervasyon listesine döner. Transient. */
  sentForApproval?: boolean;
}

async function runSearch(state: BusFlowState): Promise<Partial<BusFlowState>> {
  const { origin, destination, tripType, depDate, retDate, employee } = state.searchForm;
  const result = await searchBusTrips({
    // `Bus/Search` `departure`/`arrival` = lokasyon aramasından (`BusLocation/*`) gelen
    // `slug`; `cityCode` yalnız slug yoksa yedek.
    departure: origin?.slug || origin?.cityCode || '',
    arrival: destination?.slug || destination?.cityCode || '',
    depDate,
    // Dönüş tarihi yalnız gidiş-dönüş aramasında gönderilir.
    retDate: tripType === 'RoundTrip' ? retDate || undefined : undefined,
    employeeID: employee?.employeeID,
  });
  return { searchResult: result };
}

/** `Bus/GetBusSchema` `departureCode`/`destinationCode` + `Bus/Allocate`
 *  `journeySiraNo1`/`journeySiraNo2` = the vehicle's `binisSiraNo`/`inisSiraNo` (stop
 *  sequence, not a city code). Coerced to string for the schema request. */
const siraNo = (v: number | undefined): string => (v == null ? '' : String(v));

async function runSchema(state: BusFlowState): Promise<Partial<BusFlowState>> {
  const trip = state.selectedTrip;
  const schema = await getBusSchema({
    journeyNo: busVehicleJourneyNo(trip),
    departureCode: siraNo(trip?.binisSiraNo),
    destinationCode: siraNo(trip?.inisSiraNo),
    journeyDate: busVehicleJourneyDate(trip, state.searchForm.depDate),
    withCorridor: true,
    direction: trip?.direction,
    // Amadeus'un seferi net çözebilmesi için opak tokenlar.
    tripReference: trip?.tripReference,
    searchReference: trip?.searchReference,
    uniqueKey: trip?.uniqueKey,
  });
  return { seatSchema: schema };
}

async function runAllocate(state: BusFlowState): Promise<Partial<BusFlowState>> {
  const trip = state.selectedTrip;
  const seats = state.selectedSeats;
  const seatNo = (s: BusSeat): number => s.seatNum ?? s.seatNumber ?? Number(s.seatNo) ?? 0;
  const response = await allocateBus({
    allocatedDirections: [
      {
        journeyNo: busVehicleJourneyNo(trip),
        journeyDate: busVehicleJourneyDate(trip, state.searchForm.depDate),
        seatNoArr: seats.map(seatNo),
        referenceArr: seats.map((s) => s.reference ?? ''),
        // 'E' / 'K' — koltuk seçilirken damgalanır; belirsizse 'E'.
        genderArr: seats.map((s) => s.selectedGender ?? 'E'),
        journeySiraNo1: trip?.binisSiraNo,
        journeySiraNo2: trip?.inisSiraNo,
        shoppingCartId: state.shoppingCartId ?? null,
        uniqueKey: trip?.uniqueKey,
        direction: trip?.direction,
      },
    ],
  });
  return {
    shoppingCart: response.shoppingCart ?? undefined,
    shoppingCartId: response.shoppingCart?.id ?? state.shoppingCartId,
  };
}

/** `Bus/UpdatePassengers` her yolcuya allocate yanıtındaki `paxReferanceId`'yi (sıra ile)
 *  damgalar + legacy `BusController.UpdatePassenger` alanlarını doldurur: her yolcuya
 *  `email`/`phone` (backend her ikisini de yolcu bazında zorunlu tutuyor — form iletişimden
 *  alır, yoksa legacy'deki `?? "t@arkman.com.tr"` / `?? "+90-1112223344"` sabitlerine
 *  düşer), `paxType` "ADT", `nationality` "TR", `passportNo` "" (null değil),
 *  `selectedShuttle` "NoService", `frequentFlayerNo` "", `wheelChairService` 0. */
function withBusPaxRefs(
  passengers: Passenger[],
  cart: BusShoppingCart | undefined,
  contact: Contact | undefined
): Passenger[] {
  const refs = (cart?.busBookings ?? [])
    .flatMap((b) => b.passengerBreakdowns ?? [])
    .filter((pb) => (pb.direction ?? 'Departure') === 'Departure')
    .map((pb) => pb.paxReference?.paxReferanceId)
    .filter((id): id is string => !!id);
  return passengers.map((p, i) => ({
    ...p,
    paxReferanceId: p.paxReferanceId ?? refs[i],
    email: p.email || contact?.email || 't@arkman.com.tr',
    phone: p.phone || contact?.phone || '+90-1112223344',
    paxType: p.paxType || (p.isChild ? 'CHD' : 'ADT'),
    nationality: p.nationality || 'TR',
    passportNo: p.passportNo || '',
    selectedShuttle: p.selectedShuttle || 'NoService',
    frequentFlayerNo: p.frequentFlayerNo ?? '',
    wheelChairService: p.wheelChairService ?? 0,
  }));
}

async function runUpdatePassengers(state: BusFlowState): Promise<Partial<BusFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı — Allocate adımı tamamlanmamış.');
  const response = await updateBusPassengers({
    shoppingCartId: state.shoppingCartId,
    passengers: withBusPaxRefs(state.passengers, state.shoppingCart ?? undefined, state.contact),
    returnPassengers: [],
    contact: state.contact,
    corporatePin: null,
    notes: '',
    pickupPointExplanation: '',
    dropPointExplanation: '',
  });
  let cart = response.shoppingCart ?? state.shoppingCart;
  // Uçuş akışıyla aynı sıra: UpdatePassengers → Billing/Submit → MakePreBooking, sonra
  // ödeme adımı açılır. FinalizeShopping ödeme BAŞARILI dönünce (runPayment) yapılır.
  if (state.billingInformation && cart?.id) {
    const billingRes = await submitBilling({
      shoppingCartId: cart.id,
      billingInformation: state.billingInformation,
    });
    cart = (billingRes.shoppingCart as BusShoppingCart | null) ?? cart;
  }
  if (cart?.id) {
    const preRes = await makeBusPreBooking({ shoppingCartId: cart.id });
    // `HasError: false` + `ShoppingCart: null` de dönebiliyor — sessizce eski sepetle
    // devam etmek yerine hata fırlatıp adımı bloke ediyoruz.
    if (!preRes.shoppingCart) throw new Error('Ön rezervasyon oluşturulamadı — sepet boş döndü.');
    cart = preRes.shoppingCart;
  }
  return { shoppingCart: cart };
}

/** Ödeme sonrası biletleme — `runPayment` başarılı (3DS olmayan) ödemeden hemen sonra bunu
 *  zaten çağırır (uçuş `runPayment` deseni); bu ayrı adım yalnız o inline finalize
 *  başarısız kalırsa elle "Kesinleştir" ile tetiklenir. */
async function runFinalize(state: BusFlowState): Promise<Partial<BusFlowState>> {
  if (!state.shoppingCartId) throw new Error('Sepet bulunamadı — Allocate adımı tamamlanmamış.');
  const response = await finalizeBusShopping({ shoppingCartId: state.shoppingCartId });
  return { shoppingCart: response.shoppingCart ?? state.shoppingCart };
}

async function runPayment(state: BusFlowState): Promise<Partial<BusFlowState>> {
  if (!state.shoppingCartId) throw new Error('Ödeme için sepet bilgisi eksik.');
  const paymentType = state.paymentType ?? 'CreditCardPayment';
  if (paymentType !== 'CurrentPayment' && !state.paymentForm) {
    throw new Error('Ödeme için kart bilgileri eksik.');
  }
  // Yenilenmiş/geri dönülmüş bir akışta ödenmiş sepete ikinci kez ödeme denenmesin.
  if (cartIsPaid(state.shoppingCart)) throw new Error('Bu rezervasyon zaten ödenmiş.');
  const response = await makeBusPayment({
    shoppingCartId: state.shoppingCartId,
    amount: state.paymentAmount ?? state.shoppingCart?.remainingTotal ?? 0,
    paymentType,
    // Cari (CurrentPayment): kart yok — hesap zaten Billing/Submit ile sepete bağlandı.
    paymentForm: paymentType === 'CurrentPayment' ? undefined : state.paymentForm,
  });
  // 3DS: sayfa bankaya gidiyor — hiç çözülmeyen promise, adım ilerlemesin.
  if (redirectToBankIfNeeded(response)) return new Promise<Partial<BusFlowState>>(() => {});

  const paidCart = response.shoppingCart ?? state.shoppingCart;
  // MakePayment başarılı → FinalizeShopping (uçuş `runPayment` ile aynı). Finalize hatası
  // ödemeyi geri almaz — `payment` yine döner, kullanıcı 'finalize' adımından tekrar dener.
  return finalizeBusShopping({ shoppingCartId: state.shoppingCartId })
    .then((finRes) => ({ payment: response, shoppingCart: finRes.shoppingCart ?? paidCart }))
    .catch(() => ({ payment: response, shoppingCart: paidCart }));
}

/** No API call — final step just displays the accumulated booking/PNR/payment state. */
async function runDocument(): Promise<Partial<BusFlowState>> {
  return {};
}

export const busFlowConfig: PersistedBookingFlowConfig<BusFlowState> = {
  id: 'bus',
  initialState: {
    searchForm: { tripType: 'OneWay', depDate: '', passengerCount: 1 },
    selectedSeats: [],
    passengers: [],
  },
  persist: busPersistConfig,
  steps: [
    { id: 'search', label: 'Sefer Arama', run: runSearch },
    { id: 'schema', label: 'Koltuk Seçimi', run: runSchema },
    { id: 'allocate', label: 'Rezervasyona Al', run: runAllocate },
    // Yolcu Bilgileri = UpdatePassengers + Billing/Submit + MakePreBooking zinciri.
    { id: 'passengers', label: 'Yolcu Bilgileri', run: runUpdatePassengers },
    // Ödeme = MakePayment; başarılıysa FinalizeShopping'i de aynı adımda zincirler.
    { id: 'payment', label: 'Ödeme', run: runPayment },
    // Yalnız ödeme sonrası inline finalize başarısız kalırsa elle tetiklenen yeniden-deneme.
    { id: 'finalize', label: 'Kesinleştirme', run: runFinalize },
    { id: 'document', label: 'Belge', run: runDocument },
  ],
};
